import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  GraduationCap, CheckCircle2, Clock, Search,
  AlertTriangle, Users, Wrench
} from 'lucide-react';

export default function CloseoutTraining({ projectId, settings }) {
  const [assets, setAssets] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [search, setSearch] = useState('');
  const [scheduleForm, setScheduleForm] = useState({
    asset_id: '', trainer: '', scheduled_date: '', attendees: '', notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [assetsRes, recordsRes] = await Promise.all([
        api.closeout.listAssets(projectId),
        api.closeout.listTrainingRecords(projectId),
      ]);
      setAssets((assetsRes.data || []).filter(a => a.training_required));
      setRecords(recordsRes.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSchedule = async () => {
    if (!scheduleForm.asset_id) { toast.error('Select an asset'); return; }
    try {
      await api.closeout.createTrainingRecord(projectId, scheduleForm);
      toast.success('Training session scheduled');
      setShowSchedule(false);
      setScheduleForm({ asset_id: '', trainer: '', scheduled_date: '', attendees: '', notes: '' });
      loadData();
    } catch { toast.error('Failed to schedule'); }
  };

  const handleComplete = async (recordId) => {
    try {
      await api.closeout.completeTraining(recordId);
      toast.success('Training marked as complete');
      loadData();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const totalRequired = assets.length;
  const completed = assets.filter(a => a.training_completed).length;
  const pending = totalRequired - completed;

  const filteredRecords = records.filter(r => {
    if (!search) return true;
    return (r.asset_name || '').toLowerCase().includes(search.toLowerCase()) || (r.trainer || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-4" data-testid="closeout-training">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Training Records
          <span className="ml-2 text-sm font-normal text-slate-400">({totalRequired} assets require training)</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowSchedule(true)} data-testid="schedule-training-btn">
          <GraduationCap className="h-4 w-4" /> Schedule Session
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{totalRequired}</p><p className="text-[10px] text-slate-500 uppercase">Required</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{completed}</p><p className="text-[10px] text-slate-500 uppercase">Completed</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-600">{pending}</p><p className="text-[10px] text-slate-500 uppercase">Pending</p></CardContent></Card>
      </div>

      {/* Assets needing training */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Assets Requiring Training</h3>
          {assets.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No assets require training</p>
          ) : (
            <div className="space-y-1.5">
              {assets.map(asset => (
                <div key={asset.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 border border-slate-100" data-testid={`training-asset-${asset.id}`}>
                  <Wrench className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-400">{asset.asset_code}</span>
                      <p className="text-sm font-medium text-slate-800">{asset.asset_name}</p>
                    </div>
                    <p className="text-xs text-slate-500">{asset.asset_category?.replace(/_/g, ' ')} | {asset.location_text || 'No location'}</p>
                  </div>
                  <Badge className={`text-[10px] ${asset.training_completed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {asset.training_completed ? 'Complete' : 'Pending'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Training Records */}
      {records.length > 0 && (
        <>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input placeholder="Search records..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" />
            </div>
          </div>
          <div className="space-y-2">
            {filteredRecords.map(rec => (
              <Card key={rec.id} data-testid={`training-record-${rec.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${rec.status === 'completed' ? 'bg-green-50' : 'bg-amber-50'}`}>
                      <GraduationCap className={`h-4 w-4 ${rec.status === 'completed' ? 'text-green-600' : 'text-amber-600'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">{rec.asset_name || 'Training Session'}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {rec.trainer && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{rec.trainer}</span>}
                        {rec.scheduled_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{rec.scheduled_date}</span>}
                        {rec.attendees && <span>{rec.attendees}</span>}
                      </div>
                    </div>
                    <Badge className={`text-[10px] ${rec.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{rec.status}</Badge>
                    {rec.status === 'scheduled' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleComplete(rec.id)}>
                        <CheckCircle2 className="h-3 w-3" /> Complete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Schedule Dialog */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Schedule Training Session</DialogTitle>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Asset *</Label>
              <select className="w-full h-9 border rounded-md px-3 text-sm bg-white" value={scheduleForm.asset_id} onChange={e => setScheduleForm(f => ({...f, asset_id: e.target.value}))} data-testid="training-asset-select">
                <option value="">Select asset...</option>
                {assets.filter(a => !a.training_completed).map(a => <option key={a.id} value={a.id}>{a.asset_code} — {a.asset_name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Trainer / Instructor</Label><Input value={scheduleForm.trainer} onChange={e => setScheduleForm(f => ({...f, trainer: e.target.value}))} placeholder="Trainer name" /></div>
              <div><Label className="text-xs">Date</Label><Input type="date" value={scheduleForm.scheduled_date} onChange={e => setScheduleForm(f => ({...f, scheduled_date: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Attendees</Label><Input value={scheduleForm.attendees} onChange={e => setScheduleForm(f => ({...f, attendees: e.target.value}))} placeholder="e.g. Building Manager, FM Team" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={scheduleForm.notes} onChange={e => setScheduleForm(f => ({...f, notes: e.target.value}))} placeholder="Session notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedule(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleSchedule} data-testid="schedule-training-submit">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
